import { useCallback } from 'react';

import { gql } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import { DoBoxRule, DoboxRuleApplyTarget } from '@this+that/models';

const DO_BOX_RULES_FIELDS = `
  id
  userId
  instructions
  target
  category
  active
  createdAt
  updatedAt
`;

const GET_DO_BOX_RULES = gql`
  query doBoxRules {
    doBoxRules {
      ${DO_BOX_RULES_FIELDS}
    }
  }
`;

const CREATE_DO_BOX_RULE = gql`
  mutation createDoBoxRule($instructions: String!, $target: DoBoxRuleApplyTarget) {
    createDoBoxRule(instructions: $instructions, target: $target) {
      ${DO_BOX_RULES_FIELDS}
    }
  }
`;

const UPDATE_DO_BOX_RULE = gql`
  mutation updateDoBoxRule($id: ID!, $instructions: String, $target: DoBoxRuleApplyTarget, $active: Boolean) {
    updateDoBoxRule(id: $id, instructions: $instructions, target: $target, active: $active) {
      ${DO_BOX_RULES_FIELDS}
    }
  }
`;

const DELETE_DO_BOX_RULE = gql`
  mutation deleteDoBoxRule($id: ID!) {
    deleteDoBoxRule(id: $id)
  }
`;

const SUBMIT_DO_BOX_RULE_FEEDBACK = gql`
  mutation submitDoBoxRuleFeedback($instructions: String!, $target: DoBoxRuleApplyTarget, $context: String) {
    submitDoBoxRuleFeedback(instructions: $instructions, target: $target, context: $context) {
      accepted
    }
  }
`;

interface GetDoBoxRulesData {
  doBoxRules: DoBoxRule[];
}

interface CreateDoBoxRuleData {
  createDoBoxRule: DoBoxRule;
}

interface UpdateDoBoxRuleData {
  updateDoBoxRule: DoBoxRule;
}

interface DeleteDoBoxRuleData {
  deleteDoBoxRule: boolean;
}

interface DoBoxRuleFeedbackAck {
  accepted: boolean;
}

interface SubmitDoBoxRuleFeedbackData {
  submitDoBoxRuleFeedback: DoBoxRuleFeedbackAck;
}

export interface CreateDoBoxRuleInput {
  instructions: string;
  target?: DoboxRuleApplyTarget;
}

export interface UpdateDoBoxRuleInput {
  id: string;
  instructions?: string;
  target?: DoboxRuleApplyTarget;
  active?: boolean;
}

export interface SubmitDoBoxRuleFeedbackInput {
  instructions: string;
  target?: DoboxRuleApplyTarget;
  context?: string;
}

export function useGetDoBoxRules() {
  const { loading, refetch, data } = useQuery<GetDoBoxRulesData>(
    GET_DO_BOX_RULES,
    { skip: true },
  );

  const getDoBoxRules = useCallback(async () => {
    const { data: refetchData } = await refetch();
    return refetchData?.doBoxRules ?? [];
  }, [refetch]);

  return { getDoBoxRules, loading, rules: data?.doBoxRules ?? [] };
}

export function useCreateDoBoxRule() {
  const [createDoBoxRuleMutation, { loading, error }] =
    useMutation<CreateDoBoxRuleData>(CREATE_DO_BOX_RULE);

  const createDoBoxRule = useCallback(
    async (input: CreateDoBoxRuleInput) => {
      const { data } = await createDoBoxRuleMutation({
        variables: input,
      });
      return data?.createDoBoxRule ?? null;
    },
    [createDoBoxRuleMutation],
  );

  return { createDoBoxRule, loading, error };
}

export function useUpdateDoBoxRule() {
  const [updateDoBoxRuleMutation, { loading, error }] =
    useMutation<UpdateDoBoxRuleData>(UPDATE_DO_BOX_RULE);

  const updateDoBoxRule = useCallback(
    async (input: UpdateDoBoxRuleInput) => {
      const { data } = await updateDoBoxRuleMutation({
        variables: input,
      });
      return data?.updateDoBoxRule ?? null;
    },
    [updateDoBoxRuleMutation],
  );

  return { updateDoBoxRule, loading, error };
}

export function useDeleteDoBoxRule() {
  const [deleteDoBoxRuleMutation, { loading, error }] =
    useMutation<DeleteDoBoxRuleData>(DELETE_DO_BOX_RULE);

  const deleteDoBoxRule = useCallback(
    async (id: string) => {
      const { data } = await deleteDoBoxRuleMutation({ variables: { id } });
      return data?.deleteDoBoxRule ?? false;
    },
    [deleteDoBoxRuleMutation],
  );

  return { deleteDoBoxRule, loading, error };
}

export function useSubmitDoBoxRuleFeedback() {
  const [submitFeedbackMutation, { loading, error }] =
    useMutation<SubmitDoBoxRuleFeedbackData>(SUBMIT_DO_BOX_RULE_FEEDBACK);

  const submitDoBoxRuleFeedback = useCallback(
    async (input: SubmitDoBoxRuleFeedbackInput) => {
      const { data } = await submitFeedbackMutation({ variables: input });
      return data?.submitDoBoxRuleFeedback ?? null;
    },
    [submitFeedbackMutation],
  );

  return { submitDoBoxRuleFeedback, loading, error };
}
